import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
  headerCell: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: "bold",
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
}));

const TaskPeriodWeights = ({ task, classes }) => {
  const tableClasses = useStyles();

  const calculateWorkingDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let workingDays = 0;

    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const generatePeriods = () => {
    if (!task.plannedStartDate || !task.plannedEndDate) {
      return [];
    }

    const parseLocalDate = (dateInput) => {
      if (typeof dateInput === "string" && dateInput.includes("-")) {
        const parts = dateInput.split("T")[0].split("-");
        return new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2])
        );
      }
      return new Date(dateInput);
    };

    const startDate = parseLocalDate(task.plannedStartDate);
    const endDate = parseLocalDate(task.plannedEndDate);

    const periods = [];

    const totalWorkingDays = calculateWorkingDays(startDate, endDate);

    let currentDate = new Date(startDate);
    let periodNumber = 1;

    while (currentDate < endDate) {
      const periodStartDate = new Date(currentDate);

      const nextMonth = new Date(currentDate);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(0);

      let periodEndDate;
      if (nextMonth >= endDate) {
        periodEndDate = new Date(endDate);
      } else {
        periodEndDate = nextMonth;
      }

      const workingDays = calculateWorkingDays(periodStartDate, periodEndDate);

      periods.push({
        period: periodNumber,
        startDate: periodStartDate,
        endDate: periodEndDate,
        workingDays: workingDays,
        totalWorkingDays: totalWorkingDays,
      });

      currentDate = new Date(periodEndDate);
      currentDate.setDate(currentDate.getDate() + 1);
      periodNumber++;
    }

    const totalPeriods = periods.length;
    let cumulativeProgress = 0;
    let cumulativePlannedValue = 0;

    return periods.map((period, index) => {
      const calendarWeight =
        totalWorkingDays > 0
          ? (period.workingDays / totalWorkingDays) * 100
          : 0;

      const distributionWeight =
        totalPeriods > 0 ? (1 / totalPeriods) * 100 : 0;
      let normalizedWeight = calendarWeight;
      let periodPlannedValue = 0;

      if (task.plannedProgress && task.plannedProgress.length > 0) {
        const sortedProgress = [...task.plannedProgress].sort(
          (a, b) => a.period - b.period
        );
        const currentEntry = sortedProgress.find(
          (p) => p.period === period.period
        );

        if (currentEntry) {
          const currentProgress =
            currentEntry.expectedProgress || currentEntry.progress || 0;
          normalizedWeight = currentProgress;
          periodPlannedValue = currentEntry.expectedCost || 0;
        }
      }

      cumulativeProgress += normalizedWeight;
      cumulativePlannedValue += periodPlannedValue;

      return {
        ...period,
        calendarWeight: calendarWeight,
        distributionWeight: distributionWeight,
        normalizedWeight: normalizedWeight,
        plannedProgress: cumulativeProgress,
        plannedValue: cumulativePlannedValue,
      };
    });
  };

  const periodsData = generatePeriods();

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Period Weights
      </Typography>

      <TableContainer component={Paper}>
        <Table className={tableClasses.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell className={tableClasses.headerCell}>Period</TableCell>
              <TableCell className={tableClasses.headerCell}>
                Start Date
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                End Date
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Working Days
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Calendar Weight (%)
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Distribution Weight (%)
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Normalized Weight (%)
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Planned Progress (%)
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Planned Value ($)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periodsData.map((row) => (
              <TableRow key={row.period}>
                <TableCell className={tableClasses.tableCell}>
                  {row.period}
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {formatDate(row.startDate)}
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {formatDate(row.endDate)}
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {row.workingDays}
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {row.calendarWeight.toFixed(2)}%
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {row.distributionWeight.toFixed(2)}%
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {row.normalizedWeight.toFixed(2)}%
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  {row.plannedProgress.toFixed(2)}%
                </TableCell>
                <TableCell className={tableClasses.tableCell}>
                  ${row.plannedValue.toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {periodsData.length === 0 && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16 }}
        >
          No period data available. Please ensure the task has valid start and
          end dates.
        </Typography>
      )}
    </div>
  );
};

TaskPeriodWeights.propTypes = {
  task: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default observer(TaskPeriodWeights);
